import type { Metadata } from 'next';

import {
	CANONICAL_SITE_URL,
	DEFAULT_OG_IMAGE,
	DEFAULT_SITE_DESCRIPTION,
	DEFAULT_SITE_TITLE,
	SITE_NAME,
	TWITTER_CARD,
	TWITTER_SITE
} from '@/constants/metadata';
import { MailToConfig } from '@/types/common';

export const getMedia = (media: any) => {
	const url = media?.data?.url ?? media?.url ?? null;

	return {
		...(media?.data ?? media),
		url
	};
};

export const getBaseUrl = (): string => {
	const url = process.env.NEXT_PUBLIC_WEBSITE_URL || CANONICAL_SITE_URL;
	if (!url) throw new Error('NEXT_PUBLIC_WEBSITE_URL is not set');
	return url;
};

export interface MetadataStructureOptions {
	type?: 'website' | 'article';
	canonical?: string;
	pathPrefix?: string;
	pathname?: string;
	publishedTime?: string;
}

const normalizeCanonicalPath = (value: string): string => {
	if (value === '/') return '/';

	const path = value.startsWith('/') ? value : `/${value}`;
	return path.replace(/\/+$/, '') || '/';
};

const buildCanonicalPath = (meta: any, options?: MetadataStructureOptions): string | undefined => {
	if (options?.canonical) {
		if (options.canonical.startsWith('http'))
			try {
				return normalizeCanonicalPath(new URL(options.canonical).pathname);
			} catch {
				return undefined;
			}

		return normalizeCanonicalPath(options.canonical);
	}

	if (options?.pathname) return normalizeCanonicalPath(options.pathname);

	if (options?.pathPrefix && meta?.url)
		return normalizeCanonicalPath(`/${options.pathPrefix}/${meta.url}`);

	return undefined;
};

const buildCanonicalUrl = (canonicalPath?: string): string | undefined => {
	if (!canonicalPath) return undefined;

	const base = CANONICAL_SITE_URL.replace(/\/$/, '');
	if (canonicalPath === '/') return base;

	return `${base}${canonicalPath}`;
};

export const getMetadataStructure = (
	meta: any,
	options?: MetadataStructureOptions,
	customNoIndex?: boolean
): Metadata => {
	const title = meta?.title ?? DEFAULT_SITE_TITLE;
	const description = meta?.description ?? DEFAULT_SITE_DESCRIPTION;
	const image = getMedia(meta?.preview) ?? null;
	const imageUrl = image?.url ?? DEFAULT_OG_IMAGE.url;
	const imageWidth = image?.width ?? DEFAULT_OG_IMAGE.width;
	const imageHeight = image?.height ?? DEFAULT_OG_IMAGE.height;
	const imageAlt = image?.alt ?? DEFAULT_OG_IMAGE.alt;
	const isNoIndex = customNoIndex ?? meta?.noIndex ?? false;
	const ogType = options?.type ?? 'website';

	const canonicalPath = buildCanonicalPath(meta, options);
	const canonicalUrl = buildCanonicalUrl(canonicalPath);

	const ogImage = {
		url: imageUrl,
		width: imageWidth,
		height: imageHeight,
		alt: imageAlt
	};

	const openGraph: Metadata['openGraph'] = {
		title,
		description,
		type: ogType,
		siteName: SITE_NAME,
		...(canonicalPath && { url: canonicalUrl }),
		images: [ogImage],
		...(ogType === 'article' && options?.publishedTime
			? { publishedTime: options.publishedTime }
			: {})
	};

	return {
		robots: {
			index: !isNoIndex,
			follow: !isNoIndex
		},
		title,
		description,
		icons: {
			icon: '/favicon.ico'
		},
		...(canonicalPath && { alternates: { canonical: canonicalPath } }),
		openGraph,
		twitter: {
			card: TWITTER_CARD,
			site: TWITTER_SITE,
			title,
			description,
			images: [imageUrl]
		}
	};
};

export const playerFormatTime = (seconds: number) => {
	const roundedSeconds = Math.round(seconds);
	const minutes = Math.floor(roundedSeconds / 60);
	const remainingSeconds = roundedSeconds % 60;
	const minutesString = String(minutes).padStart(2, '0');
	const secondsString = String(remainingSeconds).padStart(2, '0');

	return `${minutesString}:${secondsString}`;
};

export const playerPercentageProgress = (value: number) => `${value * 100}%`;

export const buildMailto = ({ email, subject, message, cc, bcc }: MailToConfig): string => {
	const params: string[] = [];

	if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
	if (message) params.push(`body=${encodeURIComponent(message)}`);
	if (cc) params.push(`cc=${encodeURIComponent(cc)}`);
	if (bcc) params.push(`bcc=${encodeURIComponent(bcc)}`);

	return `mailto:${email}${params.length ? `?${params.join('&')}` : ''}`;
};
